import BookmarkIcon from "@/assets/colorable/bookmark.svg?react";
import CommentsIcon from "@/assets/colorable/comments.svg?react";
import HeartIcon from "@/assets/colorable/heart.svg?react";
import ReachIcon from "@/assets/colorable/reach.svg?react";
import ShareIcon from "@/assets/colorable/share.svg?react";
import ViewsIcon from "@/assets/colorable/views.svg?react";
import {Tooltip} from "@/components/Common/Tooltip";
import useContentIcons from "@/hooks/useContentIcons";
import {useSocialNumberFormat} from "@/hooks/useNumberFormat";
import clsxm from "@/lib/clsxm";
import type {TContent} from "@/store/types";
import {getContentPublishedDate} from "@/utils/mediaUtils";
import dayjs from "dayjs";
import isNumber from "lodash/isNumber";
import max from "lodash/max";
import {Fragment, useId, type FC} from "react";
import {useTranslation} from "react-i18next";

const ContentStatistics: FC<{
  className?: string;
  data?: {
    comment_count?: number | null;
    comments_count?: number | null;
    created?: string | null;
    impression_count?: number | null;
    like_count?: number | null;
    media_product_type?: TContent | null;
    play_count?: number | null;
    reach_count?: number | null;
    save_count?: number | null;
    saves_count?: number | null;
    share_count?: number | null;
    shares_count?: number | null;
    timestamp?: string | null;
  };
  size?: "sm" | "md";
}> = ({className, data, size = "md"}) => {
  const {t} = useTranslation(["common", "campaign_reporting"]);
  const contentIcon = useContentIcons(data?.media_product_type || "POST");
  const formatNumber = useSocialNumberFormat();
  const tooltipId = useId();
  const isSmall = size === "sm";

  const stats = [
    {Icon: ViewsIcon, label: t("views"), value: max([data?.impression_count, data?.play_count].filter(isNumber))},
    {Icon: ReachIcon, label: t("reach"), value: data?.reach_count},
    {Icon: HeartIcon, label: t("likes"), value: data?.like_count},
    {Icon: CommentsIcon, label: t("comments"), value: data?.comments_count ?? data?.comment_count},
    {Icon: ShareIcon, label: t("shares"), value: data?.share_count ?? data?.shares_count},
    {Icon: BookmarkIcon, label: t("campaign_reporting:saves"), value: data?.save_count ?? data?.saves_count},
  ].filter(stat => typeof stat.value === "number");

  const publishedAt = getContentPublishedDate({media: data});

  if (!data || (!stats.length && !publishedAt)) {
    return null;
  }

  return (
    <div
      className={clsxm(
        "text-neutral-black flex flex-wrap items-center gap-3 gap-y-1",
        isSmall && "gap-2 gap-y-0.5",
        className,
      )}>
      {!!data.media_product_type && <contentIcon.Icon className="mr-1" />}
      {!stats.length && (
        <span className={clsxm("text-sm font-semibold", isSmall && "text-2xs font-normal")}>
          {t("uploaded")} {dayjs(publishedAt).format("DD.MM.YYYY")}
        </span>
      )}
      {stats.map(({Icon, label, value}) => (
        <Fragment key={label}>
          <div className="flex items-center gap-1" data-tooltip-id={`${tooltipId}-${label}`}>
            <Icon className={clsxm("size-3.5 shrink-0", isSmall && "size-3")} />
            <span className={clsxm("text-sm font-semibold", isSmall && "text-2xs font-normal")}>
              {formatNumber(value)}
            </span>
          </div>
          <Tooltip id={`${tooltipId}-${label}`} place="top" variant="dark">
            {label}
          </Tooltip>
        </Fragment>
      ))}
    </div>
  );
};

export default ContentStatistics;
